import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Bell, CheckCircle, Download, Edit, FileText, Mail, Plus, Printer, Receipt, Save, Send, Trash2, Truck } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { EmptyState, Select, StatusBadge } from '../components/shared/UI';
import EmailModal from '../components/shared/EmailModal';
import { showAppConfirm } from '../components/shared/AppConfirm';
import SearchableProductSelect from '../components/SearchableProductSelect';
import InvoiceTemplate from '../components/InvoiceTemplate';
import { formatCurrency, formatDate } from '../lib/format';
import { useDirtyForm } from '../hooks/useDirtyForm';

const SalesModule = ({ transactions, products, customers, onAdd, onUpdate, onUpdateStatus, onDelete, settings, type, onRefresh, onSendEmail, onSendReminder, openDetailTxId, onDetailOpened }) => {
  const [view, setView] = useState('list');
  const [selectedTx, setSelectedTx] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showEmailModal, setShowEmailModal] = useState(false);

  const typeTitles = { quote: 'Quotation', invoice: 'Invoice', sj: 'Surat Jalan' };
  const moduleTitle = typeTitles[type] || 'Transaksi';

  const generateQuoteNumber = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const [formData, setFormData] = useState({
    type: type,
    customerName: '',
    customerId: '',
    customerEmail: '',
    customerAddress: '',
    quoteNumber: generateQuoteNumber(),
    date: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [],
    notes: '',
    terms: '',
    status: 'draft',
    template: 'default',
    discount: 0,
    discountType: 'fixed' // fixed or percent
  });
  const [currentItem, setCurrentItem] = useState({ productId: '', customName: '', qty: 1, price: 0 });
  const [editingTxId, setEditingTxId] = useState(null);
  const [showNoteTerms, setShowNoteTerms] = useState(true);

  // Mark form as dirty whenever we're in the create/edit view and the user has
  // entered anything meaningful (customer or at least one line item).
  const isFormDirty =
    view === 'create' &&
    (Boolean(formData.customerName) || (formData.items && formData.items.length > 0) || Boolean(formData.notes));
  useDirtyForm(isFormDirty, `Perubahan ${moduleTitle} belum disimpan. Tetap pindah halaman?`);

  // Auto-open detail when navigated from Dashboard with a pending tx ID.
  useEffect(() => {
    if (!openDetailTxId) return;
    const tx = transactions.find(t => t.id === openDetailTxId && t.type === type);
    if (tx) {
      setSelectedTx(tx);
      setView('detail');
      if (onDetailOpened) onDetailOpened();
    }
  }, [openDetailTxId, transactions, type, onDetailOpened]);

  const filteredTransactions = transactions
    .filter(t => t.type === type)
    .filter(t => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return t.customerName?.toLowerCase().includes(query) || t.id?.toLowerCase().includes(query);
      }
      return true;
    });

  const handleCustomerSelect = (e) => {
    const customerId = e.target.value;
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      // Build multi-line address supaya baris-baris yang user ketik di textarea
      // (mis. Podomoro City Ruko GSA 8DH \n Jl. Letjen S. Parman, RT.15/RW.5)
      // tetap terpisah, lalu city/state zip di baris terpisah lagi. Ini nanti
      // ditampilkan dengan whiteSpace: 'pre-line' di InvoiceTemplate.
      const useBilling = !customer.isBillingSame;
      const line1 = useBilling ? (customer.billingAddress || '') : (customer.address || '');
      const cityVal = useBilling ? customer.billingCity : customer.city;
      const stateVal = useBilling ? customer.billingState : customer.state;
      const zipVal = useBilling ? customer.billingZip : customer.zip;

      const cityStateZip = [
        [cityVal, stateVal].filter(Boolean).join(', '),
        zipVal || '',
      ].filter(Boolean).join(' ').trim();

      const addressString = [line1, cityStateZip].filter(Boolean).join('\n');

      setFormData({
        ...formData,
        customerName: customer.name,
        customerId: customer.id,
        customerEmail: customer.email || '',
        customerAddress: addressString
      });
    }
  };

  const addItem = () => {
    // Add new empty row
    const newItem = {
      productId: '',
      name: '',
      unit: 'Pcs',
      price: 0,
      qty: 1,
      total: 0
    };
    setFormData({ ...formData, items: [...formData.items, newItem] });
  };

  const removeItem = (idx) => {
    const newItems = [...formData.items];
    newItems.splice(idx, 1);
    setFormData({ ...formData, items: newItems });
  };

  const updateItemField = (idx, field, value) => {
    const newItems = [...formData.items];
    if (field === 'productId') {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[idx].productId = value;
        newItems[idx].name = product.name;
        newItems[idx].unit = product.unit;
        newItems[idx].price = product.price;
        if (!newItems[idx].qty || newItems[idx].qty < 1) newItems[idx].qty = 1;
        newItems[idx].total = product.price * newItems[idx].qty;
      } else {
        newItems[idx].productId = value;
      }
    } else if (field === 'qty' || field === 'price') {
      // Allow empty string while user is typing — don't snap to 0.
      // When empty, store empty string so input stays blank; treat as 0 only for total preview.
      if (value === '' || value === null || value === undefined) {
        newItems[idx][field] = '';
      } else {
        const parsed = parseFloat(value);
        newItems[idx][field] = Number.isFinite(parsed) ? parsed : '';
      }
      const qty = Number(newItems[idx].qty) || 0;
      const price = Number(newItems[idx].price) || 0;
      newItems[idx].total = qty * price;
    } else {
      newItems[idx][field] = value;
      newItems[idx].total = newItems[idx].price * newItems[idx].qty;
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateSubtotal = () => formData.items.reduce((sum, item) => sum + item.total, 0);
  
  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    if (formData.discountType === 'percent') {
      return Math.round(subtotal * (formData.discount / 100));
    }
    return formData.discount || 0;
  };

  const calculateTotal = () => calculateSubtotal() - calculateDiscount();

  const handleSubmit = async () => {
    if (!formData.customerName) {
      alert("Silakan pilih pelanggan terlebih dahulu.");
      return;
    }

    // Auto-clean: drop rows where no product was picked (user added blank row but didn't fill it)
    const cleanedItems = formData.items
      .filter(it => it.productId && it.name)
      .map((it, i) => {
        const qty = Number(it.qty);
        const price = Number(it.price);
        const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
        const safePrice = Number.isFinite(price) && price >= 0 ? price : 0;
        return {
          productId: it.productId,
          name: it.name,
          unit: it.unit || 'Pcs',
          price: Math.round(safePrice),
          qty: Math.max(1, Math.round(safeQty)),
          total: Math.round(safePrice * Math.max(1, safeQty)),
        };
      });

    if (cleanedItems.length === 0) {
      alert("Tambahkan minimal 1 produk dan pilih produknya dari daftar.");
      return;
    }

    // Validate item rows the user did fill: qty must be >= 1
    const invalidIdx = formData.items.findIndex(
      it => it.productId && (Number(it.qty) < 1 || !Number.isFinite(Number(it.qty)))
    );
    if (invalidIdx !== -1) {
      alert(`Qty pada baris ${invalidIdx + 1} harus minimal 1. Mohon periksa kembali.`);
      return;
    }

    const cleanedTotal = cleanedItems.reduce((s, it) => s + it.total, 0);
    const discount = formData.discountType === 'percent'
      ? Math.round(cleanedTotal * ((Number(formData.discount) || 0) / 100))
      : (Number(formData.discount) || 0);
    const finalTotal = Math.max(0, cleanedTotal - discount);

    const payload = { ...formData, items: cleanedItems, total: finalTotal };

    try {
      if (editingTxId) {
        await onUpdate(editingTxId, { ...payload, id: editingTxId });
      } else {
        await onAdd(payload);
      }
      setView('list');
      resetForm();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Save transaction failed', err);
      alert(`Gagal menyimpan: ${err?.response?.data?.detail || err?.message || 'Unknown error'}`);
    }
  };

  const resetForm = () => {
    setFormData({
      type: type,
      customerName: '',
      customerId: '',
      customerEmail: '',
      customerAddress: '',
      quoteNumber: generateQuoteNumber(),
      date: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      items: [],
      notes: '',
      terms: '',
      status: 'draft',
      template: 'default',
      discount: 0,
      discountType: 'fixed'
    });
    setCurrentItem({ productId: '', customName: '', qty: 1, price: 0 });
    setShowNoteTerms(true);
    setEditingTxId(null);
  };

  const handleEdit = (tx) => {
    // Pastikan hanya bisa edit transaksi dengan type yang sama
    if (tx.type !== type) {
      alert(`Silakan pindah ke modul ${typeTitles[tx.type] || tx.type} untuk mengedit transaksi ini.`);
      return;
    }
    
    setFormData({
      type: type, // Selalu gunakan type dari modul saat ini
      customerName: tx.customerName,
      customerId: tx.customerId || '',
      customerEmail: tx.customerEmail || '',
      customerAddress: tx.customerAddress || '',
      quoteNumber: tx.id,
      date: tx.date,
      dueDate: tx.dueDate || '',
      items: tx.items || [],
      notes: tx.notes || '',
      terms: tx.terms || '',
      status: tx.status || 'draft',
      template: tx.template || 'default',
      discount: tx.discount || 0,
      discountType: tx.discountType || 'fixed'
    });
    setEditingTxId(tx.id);
    setShowNoteTerms(!!tx.notes || !!tx.terms);
    setView('create');
  };

  const handleStatusChange = async (txId, newStatus) => {
    // Auto-create Invoice draft dari Quotation → SEKARANG DITANGANI DI BACKEND
    // (endpoint PUT /transactions/{id}/status secara idempotent membuat Invoice
    // baru ketika status quote berubah ke "converted"). Frontend cukup update
    // status saja lalu refresh — Invoice akan muncul otomatis di daftar Invoice.
    await onUpdateStatus(txId, newStatus);
    if (onRefresh) onRefresh();
  };

  const handleDelete = async (txId) => {
    if (await showAppConfirm('Yakin ingin menghapus transaksi ini?')) {
      await onDelete(txId);
      if (onRefresh) onRefresh();
    }
  };

  const handleSendReminder = async (tx) => {
    if (!onSendReminder) return;
    if (!tx.customerEmail) {
      alert('Email pelanggan belum diisi. Lengkapi data pelanggan terlebih dahulu.');
      return;
    }
    if (!(await showAppConfirm(`Kirim Payment Reminder untuk Invoice #${tx.id} ke ${tx.customerEmail}?`))) return;
    await onSendReminder(tx.id, tx.customerEmail);
  };

  const handleSendEmail = async (emailData) => {
    if (selectedTx && onSendEmail) {
      await onSendEmail(
        selectedTx.id,
        emailData.email,
        emailData.subject,
        emailData.message,
        emailData.attachmentBase64,
        emailData.attachmentFilename,
      );
    }
  };

  // Fungsi untuk membuat dokumen terkait
  const createRelatedDocument = async (targetType) => {
    if (!selectedTx) return;

    const typeLabelsIndo = { quote: 'Quotation', invoice: 'Invoice', sj: 'Surat Jalan' };
    const sourceLabel = typeLabelsIndo[selectedTx.type] || 'Dokumen';
    const targetLabel = typeLabelsIndo[targetType] || 'Dokumen';

    if (!(await showAppConfirm(`Buat ${targetLabel} dari ${sourceLabel} #${selectedTx.id}?`))) return;

    // Untuk konversi Quotation → Invoice: cukup ubah status quote ke "converted".
    // Backend akan otomatis membuat Invoice draft (dengan field sourceQuotation
    // yang benar), sehingga tidak terjadi double-create.
    if (selectedTx.type === 'quote' && targetType === 'invoice') {
      await onUpdateStatus(selectedTx.id, 'converted');
      if (onRefresh) onRefresh();
      alert(`${targetLabel} berhasil dibuat dari ${sourceLabel} #${selectedTx.id}`);
      return;
    }

    // Untuk konversi lain (mis. quote/invoice → Surat Jalan) tetap create manual
    // karena backend belum punya auto-create untuk kasus itu.
    const newDocData = {
      type: targetType,
      customerName: selectedTx.customerName,
      customerId: selectedTx.customerId || '',
      customerEmail: selectedTx.customerEmail || '',
      customerAddress: selectedTx.customerAddress || '',
      date: new Date().toISOString().split('T')[0],
      dueDate: selectedTx.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      items: selectedTx.items || [],
      notes: `Dari ${sourceLabel} #${selectedTx.id}. ${selectedTx.notes || ''}`.trim(),
      terms: selectedTx.terms || '',
      total: selectedTx.total,
      status: 'draft',
      discount: selectedTx.discount || 0,
      discountType: selectedTx.discountType || 'fixed',
      sourceDocument: { type: selectedTx.type, id: selectedTx.id }
    };

    await onAdd(newDocData);

    if (onRefresh) onRefresh();
    alert(`${targetLabel} berhasil dibuat dari ${sourceLabel} #${selectedTx.id}`);
  };

  // Detail View
  if (view === 'detail' && selectedTx) {
    const canConvertToInvoice = selectedTx.type === 'quote' && selectedTx.status !== 'converted' && selectedTx.status !== 'cancelled';
    const canCreateSuratJalan = selectedTx.type === 'invoice' && selectedTx.status !== 'cancelled';
    const useTemplate = true; // unified template for invoice / quote / sj

    const buildPdfDocument = async (nodeId = 'invoice-template') => {
      const node = document.getElementById(nodeId);
      if (!node) {
        throw new Error('Template tidak ditemukan.');
      }
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#FFFFFF',
        logging: false,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const tolerance = 0.5; // mm
      if (imgHeight <= pageHeight + tolerance) {
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, Math.min(imgHeight, pageHeight));
      } else if (imgHeight <= pageHeight * 1.15) {
        const scaledWidth = (imgWidth * pageHeight) / imgHeight;
        const xOffset = (pageWidth - scaledWidth) / 2;
        pdf.addImage(imgData, 'JPEG', xOffset, 0, scaledWidth, pageHeight);
      } else {
        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        while (heightLeft > tolerance) {
          position -= pageHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
      }
      const filename = `${(typeTitles[selectedTx.type] || 'Document')}_${selectedTx.id}.pdf`;
      return { pdf, filename };
    };

    const handleDownloadPdf = async () => {
      try {
        const { pdf, filename } = await buildPdfDocument();
        pdf.save(filename);
      } catch (err) {
        console.error('Download PDF error', err);
        alert('Gagal membuat PDF: ' + (err?.message || 'unknown error'));
      }
    };

    // Generates the same PDF (base64, no save) used by the email attachment flow.
    // For quotations, uses the hidden clone WITHOUT the status badge so the
    // customer's PDF tidak menampilkan "Draft" / "Sent" / dll.
    const generatePdfAttachment = async () => {
      const useCleanTemplate = selectedTx.type === 'quote';
      const nodeId = useCleanTemplate ? 'invoice-template-email' : 'invoice-template';
      const { pdf, filename } = await buildPdfDocument(nodeId);
      // jsPDF returns base64-encoded data when called with 'datauristring' or 'dataurl';
      // we strip the data URI prefix on the backend side.
      const dataUri = pdf.output('datauristring');
      const base64 = dataUri.includes(',') ? dataUri.split(',', 2)[1] : dataUri;
      return { base64, filename };
    };

    return (
      <div className="space-y-6 animate-in">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <button onClick={() => setView('list')} className="btn-ghost" data-testid="detail-back-btn">
            <ArrowLeft size={18}/> Kembali
          </button>
          <div className="flex flex-wrap gap-2">
            {/* Tombol Konversi/Buat Dokumen Terkait */}
            {canConvertToInvoice && (
              <button 
                onClick={() => createRelatedDocument('invoice')} 
                className="btn-secondary bg-emerald-500 text-white hover:bg-emerald-600"
                data-testid="create-invoice-btn"
              >
                <Receipt size={16}/> Buat Invoice
              </button>
            )}
            {canCreateSuratJalan && (
              <button 
                onClick={() => createRelatedDocument('sj')} 
                className="btn-secondary bg-accent text-accent-foreground hover:opacity-90"
                data-testid="create-sj-btn"
              >
                <Truck size={16}/> Buat Surat Jalan
              </button>
            )}
            <button onClick={() => { setShowEmailModal(true); }} className="btn-secondary" data-testid="email-invoice-btn">
              <Mail size={16}/> Kirim Email
            </button>
            {useTemplate && (
              <button onClick={handleDownloadPdf} className="btn-secondary" data-testid="download-pdf-btn">
                <Download size={16}/> Download PDF
              </button>
            )}
            <button onClick={() => window.print()} className="btn-primary" data-testid="print-btn">
              <Printer size={16}/> Cetak
            </button>
          </div>
        </div>

        {/* Info Sumber Dokumen */}
        {selectedTx.sourceDocument && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-800">
                Dokumen ini dibuat dari {selectedTx.sourceDocument.type === 'quote' ? 'Quotation' : 
                  selectedTx.sourceDocument.type === 'invoice' ? 'Invoice' : 'Surat Jalan'} #{selectedTx.sourceDocument.id}
              </p>
            </div>
          </div>
        )}

        {useTemplate ? (
          <>
            <div id="printable-area" className="mx-auto" style={{ width: '794px', maxWidth: '100%' }}>
              <div className="card shadow-lg overflow-hidden">
                <InvoiceTemplate transaction={selectedTx} settings={settings} products={products} />
              </div>
            </div>
            {/* Hidden clone WITHOUT status badge — dipakai khusus untuk PDF attachment email Quotation
                supaya customer TIDAK melihat status "Draft" / "Sent" / dll di PDF yang mereka terima. */}
            {selectedTx.type === 'quote' && (
              <div
                aria-hidden="true"
                style={{
                  position: 'fixed',
                  left: '-10000px',
                  top: 0,
                  width: '794px',
                  pointerEvents: 'none',
                  opacity: 0,
                }}
              >
                <InvoiceTemplate
                  transaction={selectedTx}
                  settings={settings}
                  products={products}
                  hideStatus
                  htmlId="invoice-template-email"
                />
              </div>
            )}
          </>
        ) : (
        <div id="printable-area" className="card p-8 max-w-3xl mx-auto">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-primary">{moduleTitle.toUpperCase()}</h1>
              <p className="text-muted-foreground mt-1">#{selectedTx.id}</p>
              <StatusBadge status={selectedTx.status || 'draft'} />
            </div>
            <div className="text-right">
              <h3 className="font-heading font-bold text-lg">{settings?.companyName || 'InvoicePro'}</h3>
              {settings?.address && (
                <p className="text-sm text-muted-foreground whitespace-pre-line">{settings.address}</p>
              )}
              {(settings?.city || settings?.state || settings?.zip) && (
                <p className="text-sm text-muted-foreground">
                  {[settings?.city, settings?.state].filter(Boolean).join(', ')}
                  {settings?.zip ? ` ${settings.zip}` : ''}
                </p>
              )}
              {settings?.phone && <p className="text-sm text-muted-foreground">{settings.phone}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 mb-8 p-4 bg-slate-50 rounded-xl print-info-box">
            <div>
              <p className="label">Kepada</p>
              <p className="font-semibold">{selectedTx.customerName}</p>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{selectedTx.customerAddress}</p>
              {selectedTx.customerEmail && <p className="text-sm text-muted-foreground">{selectedTx.customerEmail}</p>}
            </div>
            <div className="text-right">
              <div className="mb-2">
                <p className="label">Tanggal</p>
                <p className="font-medium">{formatDate(selectedTx.date)}</p>
              </div>
              {selectedTx.dueDate && (
                <div>
                  <p className="label">Jatuh Tempo</p>
                  <p className="font-medium">{formatDate(selectedTx.dueDate)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Table - Different layout for Surat Jalan */}
          {type === 'sj' ? (
            <>
              <table className="w-full mb-8">
                <thead className="table-header">
                  <tr>
                    <th className="px-4 py-3 text-center" style={{width: '40px'}}>No</th>
                    <th className="px-4 py-3 text-left">Item</th>
                    <th className="px-4 py-3 text-center" style={{width: '80px'}}>Qty</th>
                    <th className="px-4 py-3 text-center" style={{width: '100px'}}>Cek List</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTx.items?.map((item, idx) => (
                    <tr key={idx} className="border-b border-border/50">
                      <td className="px-4 py-3 text-center text-muted-foreground">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium">{item.name}</td>
                      <td className="px-4 py-3 text-center">{item.qty} {item.unit}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="w-6 h-6 border-2 border-slate-300 rounded mx-auto"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Penerima & Pengirim Section */}
              <div className="grid grid-cols-2 gap-8 mt-8 pt-6 border-t border-border">
                <div className="text-center">
                  <p className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-16">Penerima</p>
                  <div className="border-b border-slate-300 mx-8 mb-2"></div>
                  <p className="text-sm text-muted-foreground">Nama</p>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">Tanggal: _______________</p>
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-16">Pengirim</p>
                  <div className="border-b border-slate-300 mx-8 mb-2"></div>
                  <p className="text-sm text-muted-foreground">Nama</p>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">Tanggal: _______________</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
          <>
          <table className="w-full mb-8">
            <thead className="table-header">
              <tr>
                <th className="px-4 py-3 text-center" style={{width: '40px'}}>No</th>
                <th className="px-4 py-3 text-left">Item</th>
                <th className="px-4 py-3 text-center">Qty</th>
                <th className="px-4 py-3 text-right">Harga</th>
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {selectedTx.items?.map((item, idx) => {
                const product = products.find(p => p.id === item.productId);
                const desc = product?.description || '';
                return (
                <tr key={idx} className="border-b border-border/50">
                  <td className="px-4 py-3 text-center text-muted-foreground">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{item.name}</p>
                    {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
                  </td>
                  <td className="px-4 py-3 text-center">{item.qty} {item.unit}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatCurrency(item.price)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{formatCurrency(item.total)}</td>
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-primary text-primary-foreground">
                <td colSpan="4" className="px-4 py-4 text-right font-bold">TOTAL</td>
                <td className="px-4 py-4 text-right font-mono font-bold text-lg">{formatCurrency(selectedTx.total)}</td>
              </tr>
            </tfoot>
          </table>
          </>
          )}

          {selectedTx.notes && (
            <div className="border-t border-border pt-4">
              <p className="label">Catatan</p>
              <p className="text-sm text-muted-foreground">{selectedTx.notes}</p>
            </div>
          )}
        </div>
        )}

        <EmailModal 
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          transaction={selectedTx}
          onSend={handleSendEmail}
          generateAttachment={generatePdfAttachment}
        />
      </div>
    );
  }

  // Create View
  if (view === 'create') {
    const typeLabels = { quote: 'Quotation', invoice: 'Invoice', sj: 'Surat Jalan' };
    const typeLabelsShort = { quote: 'QUO', invoice: 'INV', sj: 'SJ' };
    const typeLabel = typeLabels[type] || 'Document';
    const typeLabelShort = typeLabelsShort[type] || 'DOC';

    return (
      <div className="space-y-6 animate-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-bold">{editingTxId ? `Edit ${typeLabel}` : `${typeLabel} Baru`}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {editingTxId ? `Mengubah data ${typeLabel.toLowerCase()} #${editingTxId}` : `Buat ${typeLabel.toLowerCase()} baru`}
            </p>
          </div>
          <button onClick={() => { setView('list'); resetForm(); }} className="btn-outline" data-testid="back-btn">
            Kembali
          </button>
        </div>

        {/* Main Form Card */}
        <div className="card border-l-4 border-l-blue-100 p-6 space-y-8">
          {/* Row 1: Client, Number, Date */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col space-y-2">
              <label className="text-sm text-muted-foreground">Pelanggan:<span className="text-destructive">*</span></label>
              <select 
                className="input-field bg-white"
                value={formData.customerId}
                onChange={handleCustomerSelect}
                data-testid="client-select"
              >
                <option value="">Pilih Pelanggan</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex flex-col space-y-2">
              <label className="text-sm text-muted-foreground">No. {typeLabelShort}</label>
              <input 
                className="input-field bg-slate-50"
                value={formData.quoteNumber}
                onChange={e => setFormData({...formData, quoteNumber: e.target.value})}
                placeholder={`Nomor ${typeLabel}`}
                readOnly={!!editingTxId}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className="text-sm text-muted-foreground">Tanggal:<span className="text-destructive">*</span></label>
              <input 
                type="date"
                className="input-field"
                value={formData.date}
                onChange={e => setFormData({...formData, date: e.target.value})}
              />
            </div>
          </div>

          {/* Row 2: Due Date, Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col space-y-2">
              <label className="text-sm text-muted-foreground">Jatuh Tempo:<span className="text-destructive">*</span></label>
              <input 
                type="date"
                className="input-field"
                value={formData.dueDate}
                onChange={e => setFormData({...formData, dueDate: e.target.value})}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className="text-sm text-muted-foreground">Status:<span className="text-destructive">*</span></label>
              <select 
                className="input-field bg-white"
                value={formData.status}
                onChange={e => setFormData({...formData, status: e.target.value})}
              >
                {type === 'quote' ? (
                  <>
                    <option value="draft">Draft</option>
                    <option value="sent">Terkirim</option>
                    <option value="converted">Dikonversi</option>
                    <option value="cancelled">Dibatalkan</option>
                  </>
                ) : type === 'sj' ? (
                  <>
                    <option value="draft">Draft</option>
                    <option value="sent">Terkirim</option>
                    <option value="cancelled">Dibatalkan</option>
                  </>
                ) : (
                  <>
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Lunas</option>
                    <option value="cancelled">Dibatalkan</option>
                  </>
                )}
              </select>
            </div>
          </div>

          {/* Add Button */}
          <div className="flex justify-end">
            <button 
              type="button" 
              onClick={addItem} 
              className="btn-primary"
              data-testid="add-item-btn"
            >
              Tambah Item
            </button>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">PRODUK<span className="text-destructive">*</span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">QTY<span className="text-destructive">*</span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">HARGA<span className="text-destructive">*</span></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">JUMLAH<span className="text-destructive">*</span></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground">AKSI</th>
                </tr>
              </thead>
              <tbody>
                {formData.items.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="px-4 py-4 text-muted-foreground">{idx + 1}</td>
                    <td className="px-4 py-4">
                      <SearchableProductSelect
                        products={products}
                        value={item.productId}
                        onChange={val => updateItemField(idx, 'productId', val)}
                        testId={`item-product-${idx}`}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <input 
                        type="number" 
                        className="input-field w-24"
                        value={item.qty || ''}
                        onChange={e => updateItemField(idx, 'qty', e.target.value)}
                        min="1"
                        placeholder="1"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <input 
                        type="number" 
                        className="input-field w-32"
                        value={item.price || ''}
                        onChange={e => updateItemField(idx, 'price', e.target.value)}
                        placeholder=""
                      />
                    </td>
                    <td className="px-4 py-4 font-mono">{formatCurrency(item.total || 0)}</td>
                    <td className="px-4 py-4 text-right">
                      <button 
                        onClick={() => removeItem(idx)} 
                        className="p-2 text-destructive hover:bg-rose-50 rounded-lg"
                      >
                        <Trash2 size={16}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Discount and Summary Section */}
          <div className="flex flex-col md:flex-row justify-between items-start gap-6">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Diskon:</span>
              <input 
                type="number"
                className="input-field w-24 bg-slate-100"
                value={formData.discount}
                onChange={e => setFormData({...formData, discount: parseInt(e.target.value) || 0})}
                placeholder="0"
              />
              <select 
                className="input-field bg-white w-44"
                value={formData.discountType}
                onChange={e => setFormData({...formData, discountType: e.target.value})}
              >
                <option value="fixed">Nominal (Rp)</option>
                <option value="percent">Persentase (%)</option>
              </select>
            </div>

            <div className="space-y-2 text-right min-w-[200px]">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Sub Total:</span>
                <span className="font-mono">{formatCurrency(calculateSubtotal())}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Diskon:</span>
                <span className="font-mono">{formatCurrency(calculateDiscount())}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold border-t pt-2">
                <span>Total:</span>
                <span className="font-mono">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>

          {/* Note & Terms Toggle */}
          <div>
            <button 
              type="button"
              onClick={() => setShowNoteTerms(!showNoteTerms)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                showNoteTerms 
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200' 
                  : 'bg-primary/10 text-primary hover:bg-primary/15 border border-primary/20'
              }`}
            >
              {showNoteTerms ? '— Sembunyikan Catatan & Syarat' : '+ Tambah Catatan & Syarat'}
            </button>
          </div>

          {/* Note & Terms Fields */}
          <AnimatePresence>
            {showNoteTerms && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div className="flex flex-col space-y-2">
                  <label className="text-sm text-muted-foreground">Catatan:</label>
                  <textarea 
                    className="input-field resize-none" 
                    rows="5"
                    value={formData.notes}
                    onChange={e => setFormData({...formData, notes: e.target.value})}
                    placeholder="Catatan tambahan..."
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm text-muted-foreground">Syarat & Ketentuan:</label>
                  <textarea 
                    className="input-field resize-none" 
                    rows="5"
                    value={formData.terms}
                    onChange={e => setFormData({...formData, terms: e.target.value})}
                    placeholder="Syarat dan ketentuan..."
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button onClick={handleSubmit} className="btn-primary" data-testid="save-transaction-btn">
              Simpan
            </button>
            <button onClick={() => { setView('list'); resetForm(); }} className="btn-secondary bg-slate-400 text-white hover:bg-slate-500">
              Batal
            </button>
          </div>
        </div>
      </div>
    );
  }


  // List View - Default for other types
  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold tracking-tight">{moduleTitle}</h2>
          <p className="text-muted-foreground mt-1">Kelola data {moduleTitle.toLowerCase()} Anda</p>
        </div>
        <button onClick={() => setView('create')} className="btn-primary" data-testid={`new-${type}-btn`}>
          <Plus size={18}/> {moduleTitle} Baru
        </button>
      </div>

      <div className="card p-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="relative w-full lg:w-80">
            <input
              type="text"
              placeholder="Cari..."
              className="input-field pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Semua Status</option>
              {type === 'quote' ? (
                <>
                  <option value="draft">Draft</option>
                  <option value="sent">Terkirim</option>
                  <option value="converted">Dikonversi</option>
                  <option value="cancelled">Dibatalkan</option>
                </>
              ) : type === 'sj' ? (
                <>
                  <option value="draft">Draft</option>
                  <option value="sent">Terkirim</option>
                  <option value="cancelled">Dibatalkan</option>
                </>
              ) : (
                <>
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Lunas</option>
                  <option value="cancelled">Dibatalkan</option>
                </>
              )}
            </Select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-4 text-left">No</th>
                <th className="px-6 py-4 text-left">ID</th>
                <th className="px-6 py-4 text-left">Pelanggan</th>
                <th className="px-6 py-4 text-center">Tanggal</th>
                <th className="px-6 py-4 text-right">Total</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-6 py-4 text-center">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="7">
                    <EmptyState 
                      title={`Belum ada ${moduleTitle.toLowerCase()}`}
                      description={`Klik tombol "${moduleTitle} Baru" untuk membuat transaksi pertama Anda`}
                    />
                  </td>
                </tr>
              ) : filteredTransactions.map((tx, index) => (
                <tr key={tx.id} className="table-row">
                  <td className="px-6 py-4 text-muted-foreground">{index + 1}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => { setSelectedTx(tx); setView('detail'); }}
                      className="font-mono font-semibold text-primary hover:underline hover:text-accent transition-colors cursor-pointer"
                      title="Lihat Detail"
                      data-testid={`tx-id-${tx.id}`}
                    >
                      #{tx.id}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium">{tx.customerName}</p>
                      <p className="text-xs text-muted-foreground">{tx.customerEmail || '-'}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center text-muted-foreground">
                    {formatDate(tx.date)}
                  </td>
                  <td className="px-6 py-4 text-right font-mono font-semibold">
                    {formatCurrency(tx.total)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <StatusBadge status={tx.status || 'draft'} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-1">
                      <button 
                        onClick={() => { setSelectedTx(tx); setView('detail'); }}
                        className="p-2 hover:bg-slate-100 rounded-lg text-muted-foreground hover:text-primary"
                        title="Lihat Detail"
                        data-testid={`view-${tx.id}`}
                      >
                        <FileText size={16}/>
                      </button>
                      <button 
                        onClick={() => handleEdit(tx)}
                        className="p-2 hover:bg-blue-50 rounded-lg text-muted-foreground hover:text-blue-600"
                        title="Edit"
                        data-testid={`edit-${tx.id}`}
                      >
                        <Edit size={16}/>
                      </button>
                      {tx.status === 'draft' && (
                        <button 
                          onClick={() => handleStatusChange(tx.id, 'sent')}
                          className="p-2 hover:bg-blue-50 rounded-lg text-muted-foreground hover:text-blue-600"
                          title="Tandai Terkirim"
                        >
                          <Send size={16}/>
                        </button>
                      )}
                      {/* Convert to Invoice - hanya untuk Quotation yang belum converted */}
                      {type === 'quote' && tx.status !== 'converted' && tx.status !== 'cancelled' && (
                        <button 
                          onClick={async () => {
                            if (await showAppConfirm(`Konversi Quotation #${tx.id} menjadi Invoice?`)) {
                              handleStatusChange(tx.id, 'converted');
                            }
                          }}
                          className="p-2 hover:bg-emerald-50 rounded-lg text-muted-foreground hover:text-emerald-600"
                          title="Konversi ke Invoice"
                          data-testid={`convert-${tx.id}`}
                        >
                          <Receipt size={16}/>
                        </button>
                      )}
                      {(tx.status === 'sent' || tx.status === 'draft') && type === 'invoice' && (
                        <button 
                          onClick={() => handleStatusChange(tx.id, 'paid')}
                          className="p-2 hover:bg-emerald-50 rounded-lg text-muted-foreground hover:text-emerald-600"
                          title="Tandai Lunas"
                        >
                          <CheckCircle size={16}/>
                        </button>
                      )}
                      {type === 'invoice' && tx.status !== 'paid' && tx.status !== 'cancelled' && (
                        <button 
                          onClick={() => handleSendReminder(tx)}
                          className="p-2 hover:bg-amber-50 rounded-lg text-muted-foreground hover:text-amber-600"
                          title="Payment Reminder Mail"
                          data-testid={`reminder-${tx.id}`}
                        >
                          <Bell size={16}/>
                        </button>
                      )}
                      <button 
                        onClick={() => handleDelete(tx.id)}
                        className="p-2 hover:bg-rose-50 rounded-lg text-muted-foreground hover:text-destructive"
                        title="Hapus"
                        data-testid={`delete-${tx.id}`}
                      >
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== SETTINGS MODULE ==============


export default SalesModule;
